import { useCallback, useEffect, useState } from 'react';

import { carregarDominio, type ChaveDeDominio, type LinhaDeDominio } from './dados';

/**
 * Os quatro estados de uma tela de domínio, num tipo só.
 *
 * O CLAUDE.md exige que toda tela tenha carregando, vazio, erro e sucesso. Um
 * tipo discriminado obriga o componente a tratar os quatro — esquecer um vira
 * erro de tipo, não tela quebrada em produção.
 */
export type EstadoDoDominio =
  | { situacao: 'carregando' }
  | { situacao: 'erro'; mensagem: string }
  | { situacao: 'vazio' }
  | { situacao: 'pronto'; linhas: LinhaDeDominio[] };

export interface RetornoDoDominio {
  estado: EstadoDoDominio;
  recarregar: () => void;
}

export function useDominio(chave: ChaveDeDominio): RetornoDoDominio {
  const [estado, setEstado] = useState<EstadoDoDominio>({ situacao: 'carregando' });
  const [tentativa, setTentativa] = useState(0);

  const recarregar = useCallback(() => {
    setTentativa((anterior) => anterior + 1);
  }, []);

  useEffect(() => {
    let vivo = true;
    setEstado({ situacao: 'carregando' });

    carregarDominio(chave)
      .then((linhas) => {
        if (!vivo) {
          return;
        }
        setEstado(linhas.length === 0 ? { situacao: 'vazio' } : { situacao: 'pronto', linhas });
      })
      .catch((erro: unknown) => {
        if (!vivo) {
          return;
        }
        setEstado({
          situacao: 'erro',
          mensagem: erro instanceof Error ? erro.message : 'Erro desconhecido.',
        });
      });

    return () => {
      // Tela trocada antes da resposta chegar: sem isto, o setState cai em
      // componente desmontado e o React avisa no console.
      vivo = false;
    };
  }, [chave, tentativa]);

  return { estado, recarregar };
}
